import Table from '@Common/Table';
import { useTestCaseContext } from '@Context/TestCaseContext';
import { PushHistory } from '@Models/types';
import { MagnifyingGlass, SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { BounceLoader, MoonLoader } from 'react-spinners';
import { PublishHistoryColumns } from './PubColumn';
import colors from '@Common/assets/colors';

const Published = () => {
  const {
    publishHistory,
    setViewType,
    setPanelOpen,
    setSelectedPublish,
    fetchPublishHistory,
    pushHistoryLoading,
  } = useTestCaseContext();

  const [search, setSearch] = useState('');
  const [filteredSearch, setFilteredSearch] =
    useState<PushHistory[]>(publishHistory);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);

    setFilteredSearch(
      publishHistory.filter((item) => {
        const testCases = JSON.parse(item.test_cases);
        const testCasesString = testCases
          .map(
            (testCase: { name: string; reference_num: string }) =>
              testCase.name + testCase.reference_num
          )
          .join(' ')
          .toLowerCase();

        return testCasesString.includes(query.toLowerCase());
      })
    );
  };

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(publishHistory);
  };

  const handleSelectPublished = (history: PushHistory) => {
    if (history.push_status === 'completed') {
      setSelectedPublish(history);
      setViewType('TestRail');
      setPanelOpen(true);
    }
  };

  useEffect(() => {
    if (publishHistory.length === 0) {
      fetchPublishHistory();
    }
  }, []);

  useEffect(() => {
    setFilteredSearch(publishHistory);
  }, [publishHistory]);

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white">
      <span className="flex w-full flex-col items-start justify-between gap-2 rounded-t-md bg-primary-700 px-4 py-2 md:flex-row md:items-center md:gap-0">
        <span className="font-extrabold text-white">Published Test Cases</span>

        <div className="flex w-full flex-row items-center justify-between rounded-md border bg-white px-2 py-1 md:w-auto">
          <div className="flex items-center gap-x-2">
            <MagnifyingGlass size={20} weight="bold" />
            <input
              type="text"
              className="border-gray-200 font-inter text-sm focus:outline-none"
              placeholder="Search"
              value={search}
              onChange={handleSearch}
              disabled={pushHistoryLoading}
            />
          </div>
          {pushHistoryLoading ? (
            <BounceLoader size={20} color={colors.brandWarning[500]} />
          ) : (
            <X
              data-testid="test-published-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          )}
        </div>
      </span>

      <div className="mx-2 mb-2 flex h-0 flex-grow flex-col">
        {!pushHistoryLoading && publishHistory.length === 0 && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-sm text-neutral-500">
              No Published Test Cases
            </label>
          </span>
        )}

        {pushHistoryLoading ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
            <MoonLoader
              data-testid="test-moon-loader"
              size={100}
              color="#071D49"
            />
            <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
              Published Test Cases Loading
            </span>
          </span>
        ) : (
          <>
            {filteredSearch && filteredSearch.length > 0 && (
              <Table
                columns={PublishHistoryColumns}
                data={filteredSearch}
                rowClickHandler={handleSelectPublished}
              />
            )}

            {publishHistory.length > 0 && filteredSearch.length === 0 && (
              <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                <SquaresFour size={100} weight="bold" />
                <label className="text-xs text-neutral-500">
                  No History Record Found
                </label>
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Published;
